import { useState, useRef, useCallback, useEffect } from 'react';
import { PaymentStatus } from '../types/Booking/booking';
import { paymentService } from '../services/paymentService';

const FINAL_STATUSES: PaymentStatus[] = ['captured', 'approved', 'authorized', 'rejected', 'refunded', 'partially_refunded', 'canceled', 'failed','no_payment'];
const MAX_ATTEMPTS_ON_PENDING = 3;

/**
 * Maps backend statuses (Spanish or uppercase) to the lowercase English values
 * expected by STATUS_MAP in the frontend.
 */
const normalizePaymentStatus = (raw: string): PaymentStatus => {
    const BACKEND_STATUS_MAP: Record<string, PaymentStatus> = {
        // Spanish statuses from Plexo/backend
        'INICIADO':            'pending',
        'AUTORIZADO':          'authorized',
        'CAPTURADO':           'captured',
        'APROBADO':            'approved',
        'RECHAZADO':           'rejected',
        'CANCELADO':           'canceled',
        'FALLIDO':             'failed',
        'REEMBOLSADO':         'refunded',
        'PENDIENTE':           'pending',
        'PROCESANDO':          'pending',
        // Uppercase English that might come from backend
        'PENDING':             'pending',
        'AUTHORIZED':          'authorized',
        'CAPTURED':            'captured',
        'APPROVED':            'approved',
        'REJECTED':            'rejected',
        'CANCELED':            'canceled',
        'CANCELLED':           'canceled',
        'FAILED':              'failed',
        'REFUNDED':            'refunded',
        'PROCESSING':          'pending',
        'NO_PAYMENT':          'no_payment',
    };
    return BACKEND_STATUS_MAP[raw] || (raw.toLowerCase() as PaymentStatus);
};

export const usePaymentStatus = () => {
    const [status, setStatus] = useState<PaymentStatus | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const attemptsRef = useRef<number>(0);

    const stopPolling = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        setIsLoading(false);
    }, []);

    const startPolling = useCallback(async (bookingUid: string) => {
        setIsLoading(true);
        setError(null);
        setStatus('initiating');
        attemptsRef.current = 0;

        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

    const checkStatus = async () => {
        try {
            const response = await paymentService.getPaymentStatus(bookingUid);
            // Normalize status from backend (may be Spanish or uppercase)
            const normalized = normalizePaymentStatus(response.status as string);
            setStatus(normalized);
            
            if (normalized === 'pending') {
                attemptsRef.current += 1;
            }
            
            const isFinalStatus = FINAL_STATUSES.includes(normalized);
            
            if (isFinalStatus || attemptsRef.current >= MAX_ATTEMPTS_ON_PENDING) {
                stopPolling();
            }
        } catch (err: any) {
            setError(err.message || 'Error al verificar el estado del pago.');
            setStatus('failed');
            stopPolling();
        }
    };

        await checkStatus();


        if (attemptsRef.current < MAX_ATTEMPTS_ON_PENDING && intervalRef.current === null) {
            intervalRef.current = setInterval(checkStatus, 3000);
        }

    }, [stopPolling]);

    useEffect(() => {
        return () => {
            stopPolling();
        };
    }, [stopPolling]);

    return { status, isLoading, error, startPolling, stopPolling };
};